import React, { useState, useRef, Component } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { CheckCircleIcon, ArrowRightIcon, UsersIcon, DollarSignIcon, TrendingUpIcon, ShieldCheckIcon, MessageSquareIcon } from 'lucide-react';
// ─── Animation Wrapper ───────────────────────────────────────────────────────
function FadeInSection({
  children,
  delay = 0
}: {
  children: React.ReactNode;
  delay?: number;
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  return <motion.div ref={ref} initial={{
    opacity: 0,
    y: 32
  }} animate={isInView ? {
    opacity: 1,
    y: 0
  } : {}} transition={{
    duration: 0.6,
    delay,
    ease: [0.22, 1, 0.36, 1]
  }} data-id="element-139">
      {children}
    </motion.div>;
}
// ─── Hero Section ───────────────────────────────────────────────────────────
function HeroSection() {
  return <section className="relative min-h-screen flex flex-col items-center justify-center bg-dark grid-bg overflow-hidden px-4 sm:px-6 lg:px-8 py-24" data-id="element-140">
      {/* Gold cloud glow */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none" aria-hidden="true" data-id="element-141">
        <div className="w-[700px] h-[500px] rounded-full blur-3xl opacity-100" style={{
        background: 'radial-gradient(ellipse at center, rgba(176,141,87,0.18) 0%, transparent 70%)'
      }} data-id="element-142" />
      </div>

      <div className="relative z-10 max-w-4xl mx-auto text-center" data-id="element-143">
        <FadeInSection data-id="element-144">
          <div className="inline-block border border-accent/40 text-accent text-xs tracking-widest uppercase rounded-full px-4 py-1.5 mb-8 font-body" data-id="element-145">
            PROGRAMA DE AFILIADOS BTS™
          </div>
        </FadeInSection>

        <FadeInSection delay={0.1} data-id="element-146">
          <h1 className="font-editorial font-bold text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-tight text-bg mb-6" data-id="element-147">
            Construiste tu Sistema. <br className="hidden sm:block" data-id="element-148" />
            Ahora <span className="text-accent" data-id="element-149">Gana</span> Recomendándolo.
          </h1>
        </FadeInSection>

        <FadeInSection delay={0.2} data-id="element-150">
          <p className="font-body text-bg/70 text-lg sm:text-xl max-w-2xl mx-auto mb-10 leading-relaxed" data-id="element-151">
            Cada persona que refieras a BTS™ te genera{' '}
            <strong className="text-bg font-semibold" data-id="element-152">
              $250 de comisión directa
            </strong>{' '}
            — además de tus overrides por su producción.
          </p>
        </FadeInSection>

        <FadeInSection delay={0.3} data-id="element-153">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-154">
            <Link to="/contact-us" className="w-full sm:w-auto inline-flex items-center justify-center bg-accent hover:bg-accent-dark text-dark font-body font-semibold text-base rounded-full px-8 py-4 transition-colors duration-200" data-id="element-155">
              QUIERO SER AFILIADO
            </Link>
          </div>
        </FadeInSection>
      </div>
    </section>;
}
// ─── Qué Es Section ─────────────────────────────────────────────────────────
function QueEsSection() {
  return <section className="py-24 px-4 sm:px-6 lg:px-8 bg-bg grid-bg-light" data-id="element-156">
      <div className="max-w-4xl mx-auto text-center" data-id="element-157">
        <FadeInSection data-id="element-158">
          <h2 className="font-editorial font-bold text-3xl sm:text-4xl md:text-5xl text-dark mb-8" data-id="element-159">
            ¿Qué es el Programa de Afiliados BTS™?
          </h2>
        </FadeInSection>

        <FadeInSection delay={0.1} data-id="element-160">
          <div className="prose prose-lg mx-auto font-body text-dark/70 leading-relaxed space-y-6" data-id="element-161">
            <p data-id="element-162">
              Si ya completaste BTS™ y estás en SCALE como Constructor, tienes
              acceso al programa de afiliados. Es simple: cada vez que alguien
              que TÚ refieres compra el paquete BTS™ ($2,500), tú recibes el{' '}
              <strong className="text-accent font-bold" data-id="element-163">10%</strong> —{' '}
              <strong className="text-accent font-bold" data-id="element-164">$250</strong> —
              directamente.
            </p>
            <p data-id="element-165">
              No es MLM. No es pirámide. Es un programa de referidos donde ganas
              por recomendar algo que ya viviste y que ya sabes que funciona.
            </p>
          </div>
        </FadeInSection>
      </div>
    </section>;
}
// ─── Cómo Funciona Section ──────────────────────────────────────────────────
function ComoFuncionaSection() {
  const steps = [{
    num: '1',
    title: 'Regístrate',
    desc: 'Te registras como afiliado y recibes tu link personalizado.'
  }, {
    num: '2',
    title: 'Comparte',
    desc: 'Compartes tu link con personas que podrían beneficiarse de BTS™ — reclutas potenciales, agentes que conoces, gente en tu red.'
  }, {
    num: '3',
    title: 'Refiere',
    desc: 'Cuando alguien compra BTS™ a través de tu link, se registra automáticamente como tu referido.'
  }, {
    num: '4',
    title: 'Gana',
    desc: 'Recibes tu comisión del 10% ($250 por cada referido que compre).'
  }];
  return <section className="py-24 px-4 sm:px-6 lg:px-8 bg-dark" data-id="element-166">
      <div className="max-w-6xl mx-auto" data-id="element-167">
        <div className="text-center mb-16" data-id="element-168">
          <FadeInSection data-id="element-169">
            <div className="inline-block border border-accent/30 text-accent text-xs tracking-widest uppercase rounded-full px-4 py-1.5 mb-5 font-body" data-id="element-170">
              El Proceso
            </div>
            <h2 className="font-editorial font-bold text-3xl sm:text-4xl md:text-5xl text-bg" data-id="element-171">
              Cómo Funciona
            </h2>
          </FadeInSection>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-172">
          {steps.map((step, i) => <FadeInSection key={i} delay={i * 0.1} data-id="element-173">
              <div className="bg-bg/5 border border-white/8 rounded-2xl p-8 h-full relative overflow-hidden group hover:border-accent/30 transition-colors duration-300" data-id="element-174">
                <div className="absolute -right-4 -top-4 text-9xl font-editorial font-bold text-white/[0.02] group-hover:text-accent/[0.05] transition-colors duration-300 pointer-events-none" data-id="element-175">
                  {step.num}
                </div>
                <div className="relative z-10" data-id="element-176">
                  <div className="w-12 h-12 rounded-full bg-accent/15 flex items-center justify-center mb-6 border border-accent/20" data-id="element-177">
                    <span className="font-editorial font-bold text-accent text-xl" data-id="element-178">
                      {step.num}
                    </span>
                  </div>
                  <h3 className="font-editorial font-bold text-xl text-bg mb-3" data-id="element-179">
                    Step {step.num}: {step.title}
                  </h3>
                  <p className="font-body text-bg/60 text-sm leading-relaxed" data-id="element-180">
                    {step.desc}
                  </p>
                </div>
              </div>
            </FadeInSection>)}
        </div>
      </div>
    </section>;
}
// ─── Ingreso Doble Section ──────────────────────────────────────────────────
function IngresoDobleSection() {
  return <section className="py-24 px-4 sm:px-6 lg:px-8 bg-bg grid-bg-light" data-id="element-181">
      <div className="max-w-5xl mx-auto" data-id="element-182">
        <div className="text-center mb-16" data-id="element-183">
          <FadeInSection data-id="element-184">
            <h2 className="font-editorial font-bold text-3xl sm:text-4xl md:text-5xl text-dark mb-4" data-id="element-185">
              Tu Ingreso Doble como Constructor
            </h2>
          </FadeInSection>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12" data-id="element-186">
          <FadeInSection delay={0.1} data-id="element-187">
            <div className="bg-dark rounded-2xl p-8 border border-white/5 shadow-xl" data-id="element-188">
              <div className="flex items-center gap-4 mb-6 pb-6 border-b border-white/10" data-id="element-189">
                <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center" data-id="element-190">
                  <TrendingUpIcon className="w-6 h-6 text-bg/80" data-id="element-191" />
                </div>
                <h3 className="font-editorial font-bold text-2xl text-bg" data-id="element-192">
                  Overrides por producción
                </h3>
              </div>
              <ul className="space-y-4" data-id="element-193">
                <li className="flex items-start gap-3" data-id="element-194">
                  <CheckCircleIcon className="w-5 h-5 text-accent shrink-0 mt-0.5" data-id="element-195" />
                  <span className="font-body text-bg/70" data-id="element-196">
                    Tus reclutas venden pólizas → tú ganas override por cada
                    venta
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-197">
                  <CheckCircleIcon className="w-5 h-5 text-accent shrink-0 mt-0.5" data-id="element-198" />
                  <span className="font-body text-bg/70" data-id="element-199">
                    Variable según producción del equipo
                  </span>
                </li>
              </ul>
            </div>
          </FadeInSection>

          <FadeInSection delay={0.2} data-id="element-200">
            <div className="bg-dark rounded-2xl p-8 border border-accent/30 shadow-xl relative overflow-hidden" data-id="element-201">
              <div className="absolute inset-0 bg-gradient-to-br from-accent/10 to-transparent pointer-events-none" data-id="element-202" />
              <div className="relative z-10" data-id="element-203">
                <div className="flex items-center gap-4 mb-6 pb-6 border-b border-white/10" data-id="element-204">
                  <div className="w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center" data-id="element-205">
                    <DollarSignIcon className="w-6 h-6 text-accent" data-id="element-206" />
                  </div>
                  <h3 className="font-editorial font-bold text-2xl text-bg" data-id="element-207">
                    Comisión afiliado BTS™
                  </h3>
                </div>
                <ul className="space-y-4" data-id="element-208">
                  <li className="flex items-start gap-3" data-id="element-209">
                    <CheckCircleIcon className="w-5 h-5 text-accent shrink-0 mt-0.5" data-id="element-210" />
                    <span className="font-body text-bg/70" data-id="element-211">
                      Cada recluta que compra BTS™ por tu link →{' '}
                      <strong className="text-accent" data-id="element-212">$250</strong>
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-213">
                    <CheckCircleIcon className="w-5 h-5 text-accent shrink-0 mt-0.5" data-id="element-214" />
                    <span className="font-body text-bg/70" data-id="element-215">
                      10 referidos ={' '}
                      <strong className="text-accent" data-id="element-216">$2,500/mes</strong>
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </FadeInSection>
        </div>

        <FadeInSection delay={0.3} data-id="element-217">
          <div className="max-w-3xl mx-auto text-center space-y-6" data-id="element-218">
            <p className="font-body text-dark/70 text-lg leading-relaxed" data-id="element-219">
              Son dos fuentes de ingreso simultáneas. Tu equipo produce pólizas
              (overrides para ti) y cada nuevo integrante que entra al sistema
              (comisión de afiliado para ti).
            </p>
            <div className="bg-accent/10 border border-accent/20 rounded-xl p-6" data-id="element-220">
              <p className="font-body text-dark font-medium leading-relaxed" data-id="element-221">
                Un constructor que refiere 10 personas al mes a BTS™ genera{' '}
                <strong className="text-accent-hover" data-id="element-222">
                  $2,500 solo en comisiones de afiliado
                </strong>{' '}
                — antes de contar los overrides por producción de su equipo. Eso
                paga el programa de vuelta todos los meses.
              </p>
            </div>
          </div>
        </FadeInSection>
      </div>
    </section>;
}
// ─── Quién Puede Ser Afiliado Section ───────────────────────────────────────
function RequisitosSection() {
  return <section className="py-24 px-4 sm:px-6 lg:px-8 bg-dark" data-id="element-223">
      <div className="max-w-4xl mx-auto" data-id="element-224">
        <FadeInSection data-id="element-225">
          <div className="text-center mb-12" data-id="element-226">
            <h2 className="font-editorial font-bold text-3xl sm:text-4xl md:text-5xl text-bg mb-6" data-id="element-227">
              ¿Quién Puede Ser Afiliado?
            </h2>
            <p className="font-body text-bg/60 text-lg" data-id="element-228">
              El programa está disponible para agentes que:
            </p>
          </div>
        </FadeInSection>

        <div className="bg-bg/5 border border-white/8 rounded-2xl p-8 sm:p-12 mb-8" data-id="element-229">
          <ul className="space-y-6" data-id="element-230">
            <FadeInSection delay={0.1} data-id="element-231">
              <li className="flex items-center gap-4" data-id="element-232">
                <div className="w-8 h-8 rounded-full bg-accent/20 flex items-center justify-center shrink-0" data-id="element-233">
                  <CheckCircleIcon className="w-5 h-5 text-accent" data-id="element-234" />
                </div>
                <span className="font-body text-bg text-lg" data-id="element-235">
                  Completaron el programa BTS™
                </span>
              </li>
            </FadeInSection>
            <FadeInSection delay={0.2} data-id="element-236">
              <li className="flex items-center gap-4" data-id="element-237">
                <div className="w-8 h-8 rounded-full bg-accent/20 flex items-center justify-center shrink-0" data-id="element-238">
                  <CheckCircleIcon className="w-5 h-5 text-accent" data-id="element-239" />
                </div>
                <span className="font-body text-bg text-lg" data-id="element-240">
                  Están activos en SCALE como Constructores
                </span>
              </li>
            </FadeInSection>
            <FadeInSection delay={0.3} data-id="element-241">
              <li className="flex items-center gap-4" data-id="element-242">
                <div className="w-8 h-8 rounded-full bg-accent/20 flex items-center justify-center shrink-0" data-id="element-243">
                  <CheckCircleIcon className="w-5 h-5 text-accent" data-id="element-244" />
                </div>
                <span className="font-body text-bg text-lg" data-id="element-245">
                  Tienen experiencia real con el sistema (ya vivieron BUILD +
                  TRACK)
                </span>
              </li>
            </FadeInSection>
          </ul>
        </div>

        <FadeInSection delay={0.4} data-id="element-246">
          <p className="font-body text-bg/50 text-center italic" data-id="element-247">
            No puedes recomendar algo que no has vivido. Por eso el programa es
            exclusivo para graduados de BTS™.
          </p>
        </FadeInSection>
      </div>
    </section>;
}
// ─── Por Qué Funciona Section ───────────────────────────────────────────────
function PorQueFuncionaSection() {
  const benefits = [{
    icon: ShieldCheckIcon,
    text: 'Ya lo viviste — puedes hablar con autoridad sobre lo que BTS™ hace'
  }, {
    icon: MessageSquareIcon,
    text: 'Tu recluta te va a preguntar si funciona — y tú puedes responder con tu historia real'
  }, {
    icon: UsersIcon,
    text: 'No necesitas ser vendedor — necesitas compartir lo que ya sabes'
  }];
  return <section className="py-24 px-4 sm:px-6 lg:px-8 bg-bg grid-bg-light" data-id="element-248">
      <div className="max-w-5xl mx-auto" data-id="element-249">
        <div className="text-center mb-16" data-id="element-250">
          <FadeInSection data-id="element-251">
            <h2 className="font-editorial font-bold text-3xl sm:text-4xl md:text-5xl text-dark mb-6" data-id="element-252">
              Por Qué Funciona
            </h2>
            <p className="font-body text-dark/70 text-lg max-w-2xl mx-auto" data-id="element-253">
              No estás vendiendo un producto genérico. Estás recomendando el
              mismo sistema que te cambió la forma de operar. Tu credibilidad
              viene de tu experiencia.
            </p>
          </FadeInSection>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-254">
          {benefits.map((benefit, i) => <FadeInSection key={i} delay={i * 0.1} data-id="element-255">
              <div className="bg-dark rounded-2xl p-8 border border-white/5 h-full flex flex-col items-center text-center hover:border-accent/20 transition-colors duration-300" data-id="element-256">
                <div className="w-14 h-14 rounded-full bg-accent/10 flex items-center justify-center mb-6" data-id="element-257">
                  <benefit.icon className="w-7 h-7 text-accent" data-id="element-258" />
                </div>
                <p className="font-body text-bg/80 leading-relaxed" data-id="element-259">
                  {benefit.text}
                </p>
              </div>
            </FadeInSection>)}
        </div>
      </div>
    </section>;
}
// ─── FAQ Section ─────────────────────────────────────────────────────────────
type FAQItemType = {
  question: string;
  answer: string;
};
const faqItems: FAQItemType[] = [{
  question: '¿Cuándo recibo mi comisión?',
  answer: 'Una vez que el pago del referido se confirma, tu comisión se procesa en el siguiente ciclo de pagos.'
}, {
  question: '¿Hay límite de referidos?',
  answer: 'No. Puedes referir a tantas personas como quieras. No hay techo.'
}, {
  question: '¿Qué pasa si mi referido pide reembolso?',
  answer: 'Si el referido solicita reembolso dentro del período establecido, la comisión se ajusta.'
}, {
  question: '¿Necesito hacer algo especial para ser afiliado?',
  answer: 'Solo necesitas haber completado BTS™ y estar en SCALE. Se te asigna tu link automáticamente.'
}, {
  question: '¿Puedo combinarlo con mis overrides?',
  answer: 'Sí. Son ingresos separados. La comisión de afiliado es por la venta del paquete BTS™. Los overrides son por la producción de pólizas de tu equipo. Ambos son tuyos.'
}];
function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  return <section className="py-24 px-4 sm:px-6 lg:px-8 bg-dark" data-id="element-260">
      <div className="max-w-3xl mx-auto" data-id="element-261">
        <div className="text-center mb-14" data-id="element-262">
          <FadeInSection data-id="element-263">
            <div className="inline-block border border-accent/40 text-accent text-xs tracking-widest uppercase rounded-full px-4 py-1.5 mb-5 font-body" data-id="element-264">
              Preguntas frecuentes
            </div>
            <h2 className="font-editorial font-bold text-3xl sm:text-4xl md:text-5xl text-bg" data-id="element-265">
              Resolvemos tus dudas
            </h2>
          </FadeInSection>
        </div>

        <div className="space-y-3" data-id="element-266">
          {faqItems.map((item, index) => <FadeInSection key={index} delay={index * 0.1} data-id="element-267">
              <div className="bg-bg/5 border border-white/8 rounded-xl overflow-hidden" data-id="element-268">
                <button className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left hover:bg-white/[0.03] transition-colors duration-200" onClick={() => setOpenIndex(openIndex === index ? null : index)} aria-expanded={openIndex === index} data-id="element-269">
                  <span className="font-body font-semibold text-bg text-sm sm:text-base leading-snug" data-id="element-270">
                    {item.question}
                  </span>
                  <span className={`flex-shrink-0 w-6 h-6 rounded-full border border-accent/30 flex items-center justify-center transition-transform duration-300 ${openIndex === index ? 'rotate-45' : ''}`} aria-hidden="true" data-id="element-271">
                    <svg className="w-3 h-3 text-accent" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" data-id="element-272">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" data-id="element-273" />
                    </svg>
                  </span>
                </button>
                <div className={`accordion-content ${openIndex === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-274">
                  <div className="px-6 pb-5" data-id="element-275">
                    <p className="font-body text-bg/60 text-sm leading-relaxed" data-id="element-276">
                      {item.answer}
                    </p>
                  </div>
                </div>
              </div>
            </FadeInSection>)}
        </div>
      </div>
    </section>;
}
// ─── Final CTA Section ──────────────────────────────────────────────────────
function FinalCTASection() {
  return <section className="relative py-24 px-4 sm:px-6 lg:px-8 bg-dark overflow-hidden border-t border-white/5" data-id="element-277">
      {/* Subtle glow */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none" aria-hidden="true" data-id="element-278">
        <div className="w-[600px] h-[300px] rounded-full blur-3xl" style={{
        background: 'radial-gradient(ellipse at center, rgba(176,141,87,0.12) 0%, transparent 70%)'
      }} data-id="element-279" />
      </div>

      <div className="relative z-10 max-w-3xl mx-auto text-center" data-id="element-280">
        <FadeInSection data-id="element-281">
          <div className="inline-block border border-accent/30 text-accent text-xs tracking-widest uppercase rounded-full px-4 py-1.5 mb-6 font-body" data-id="element-282">
            Actúa Ahora
          </div>
          <h2 className="font-editorial font-bold text-3xl sm:text-4xl md:text-5xl text-bg mb-5 leading-tight" data-id="element-283">
            Ya Construiste tu Sistema. <br data-id="element-284" />
            Ahora <span className="text-accent" data-id="element-285">Multiplica tu Ingreso</span>.
          </h2>
          <p className="font-body text-bg/60 text-lg mb-10 max-w-xl mx-auto leading-relaxed" data-id="element-286">
            Cada persona que entra a BTS™ por tu recomendación es $250 en tu
            bolsillo + un nuevo miembro de tu equipo generando producción. El
            sistema ya funciona — ahora compártelo.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-287">
            <Link to="/contact-us" className="w-full sm:w-auto inline-flex items-center justify-center bg-accent hover:bg-accent-dark text-dark font-body font-semibold text-base rounded-full px-8 py-4 transition-colors duration-200" data-id="element-288">
              ACTIVAR MI LINK DE AFILIADO
            </Link>
          </div>
          <p className="font-body text-bg/30 text-xs mt-8" data-id="element-289">
            Reclutas Digitales — BTS™ Método Arquitecto™ — Programa de
            Afiliados
          </p>
        </FadeInSection>
      </div>
    </section>;
}
// ─── Main Page Component ────────────────────────────────────────────────────
export function AfiliadosPage() {
  return <div className="w-full" data-id="element-290">
      <HeroSection data-id="element-291" />
      <QueEsSection data-id="element-292" />
      <ComoFuncionaSection data-id="element-293" />
      <IngresoDobleSection data-id="element-294" />
      <RequisitosSection data-id="element-295" />
      <PorQueFuncionaSection data-id="element-296" />
      <FAQSection data-id="element-297" />
      <FinalCTASection data-id="element-298" />
    </div>;
}